import { ChartOptions, Plugin } from 'chart.js'

/**
 * Common chart colors used across all charts
 */
export const CHART_COLORS = {
  primary: {
    background: 'rgba(53, 162, 235, 0.5)',
    border: 'rgb(53, 162, 235)'
  },
  secondary: {
    background: 'rgba(255, 99, 132, 0.5)',
    border: 'rgb(255, 99, 132)'
  },
  tertiary: {
    background: 'rgba(255, 159, 64, 0.5)',
    border: 'rgb(255, 159, 64)'
  },
  success: {
    background: 'rgba(75, 192, 192, 0.5)',
    border: 'rgb(75, 192, 192)'
  },
  warning: {
    background: 'rgba(255, 205, 86, 0.5)',
    border: 'rgb(255, 205, 86)'
  },
  info: {
    background: 'rgba(54, 162, 235, 0.5)',
    border: 'rgb(54, 162, 235)'
  }
} as const

/**
 * Base chart options that are common across all charts
 */
export const getBaseChartOptions = (title?: string): Partial<ChartOptions> => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top' as const,
    },
    ...(title && {
      title: {
        display: true,
        text: title,
        font: {
          size: 16,
        }
      }
    })
  }
})

/**
 * Common scale configuration for X axis
 */
export const getXAxisConfig = (
  title: string,
  labels?: string[]
) => ({
  title: {
    display: true,
    text: title,
    font: {
      weight: 'bold' as const
    }
  },
  ...(labels && {
    ticks: {
      autoSkip: false
    }
  })
})

/**
 * Common scale configuration for Y axis
 */
export const getYAxisConfig = (
  title: string,
  options?: {
    beginAtZero?: boolean
    grace?: string
    position?: 'left' | 'right'
    drawOnChartArea?: boolean
    min?: number
    max?: number
    stepSize?: number
    tickCallback?: (value: any) => string | void
  }
) => ({
  title: {
    display: true,
    text: title,
    font: {
      weight: 'bold' as const
    }
  },
  ...(options?.beginAtZero !== undefined && { beginAtZero: options.beginAtZero }),
  ...(options?.grace && { grace: options.grace }),
  ...(options?.position && { position: options.position }),
  ...(options?.min !== undefined && { min: options.min }),
  ...(options?.max !== undefined && { max: options.max }),
  ...(options?.stepSize && { 
    ticks: {
      stepSize: options.stepSize,
      ...(options.tickCallback && { callback: options.tickCallback })
    }
  }),
  ...(options?.drawOnChartArea === false && {
    grid: {
      drawOnChartArea: false,
    }
  })
})

/**
 * Common tooltip configuration
 */
export const getTooltipConfig = (
  customCallbacks?: any
) => ({
  ...(customCallbacks && { callbacks: customCallbacks }),
  intersect: false,
  enabled: true,
})

/**
 * Utility to get max value for percentage charts with padding
 */
export const getMaxValueWithPadding = (values: (number | null)[], defaultMax = 100): number => {
  const validValues = values.filter(val => val !== null) as number[]
  if (validValues.length === 0) return defaultMax
  
  const maxValue = Math.max(...validValues)
  return Math.min(Math.max(maxValue + 10, defaultMax), defaultMax + 10)
}

/**
 * Common data labels plugin configuration
 */
export const createDataLabelsPlugin = (
  datasetIndex: number,
  color: string,
  options?: {
    textAlign?: 'left' | 'center' | 'right' | 'end'
    textBaseline?: 'top' | 'middle' | 'bottom'
    yOffset?: number
    format?: (value: number) => string
  }
): Plugin => ({
  id: `dataLabels_${datasetIndex}`,
  afterDatasetsDraw(chart) {
    const { ctx } = chart
    const dataset = chart.data.datasets[datasetIndex]
    if (!dataset) return
    
    ctx.save()
    ctx.font = '12px Arial'
    ctx.fillStyle = color
    ctx.textAlign = options?.textAlign || 'center'
    ctx.textBaseline = options?.textBaseline || 'middle'
    
    const meta = chart.getDatasetMeta(datasetIndex)
    
    meta.data.forEach((element, index) => {
      const value = dataset.data[index]
      
      if (value !== null && value !== undefined) {
        const { x, y } = element
        const numValue = typeof value === 'object' ? 
          (value as any).y || 0 : 
          Number(value)
        
        const yOffset = options?.yOffset || 0
        const formattedValue = options?.format ? 
          options.format(numValue) : 
          `${numValue}%`
        
        ctx.fillText(formattedValue, x, y + yOffset)
      }
    })
    
    ctx.restore()
  }
})